// Hasta çalışma alanı "Hesap" sekmesi (billing modülü) — kartlı dil (design-principles §4):
// üstte bakiye özeti, altında paketler, ödemeler ve hizmet geçmişi. Tutarlar kuruş.
import React, { useCallback, useEffect, useState } from "react";
import { Box, IconButton, LinearProgress, Tooltip, Typography, alpha } from "@mui/material";
import {
  Inventory2Outlined as PackageIcon,
  PaymentsOutlined as PaymentIcon,
  MedicalServicesOutlined as ServiceIcon,
  DeleteOutline as DeleteIcon,
} from "@mui/icons-material";
import KpiTile from "../../molecules/KpiTile";
import { PromptDialog } from "../../molecules";
import { ActionButton } from "../../atoms";
import { AnalyticsCard, BodySkeleton, CardEmpty } from "../charts/analyticsParts";
import { StatusPill } from "../ClinicRecordList";
import SellPackageDrawer from "../Billing/SellPackageDrawer";
import PaymentDrawer from "../Billing/PaymentDrawer";
import PackageDetailDrawer from "../Billing/PackageDetailDrawer";
import { PACKAGE_STATUS, dateTr } from "../Billing/billingParts";
import { useClinicTheme } from "../../../theme";
import { useAlert } from "../../../context/AlertProvider";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { COLORS, TYPO, CARD_GAP } from "../../../styles/uiConstants";
import { formatMoney, PAYMENT_METHOD_LABELS } from "../../../utils/money";
import {
  useGetPatientAccountMutation,
  useSellPatientPackageMutation,
  useConsumePatientPackageMutation,
  useRevertPackageUsageMutation,
  useCancelPatientPackageMutation,
  useCreatePaymentMutation,
  useDeletePaymentMutation,
} from "../../../store/billingApi";
import useTerms from "../../../hooks/useTerms";

const BODY = 340;
const STATUS_LABELS = {
  scheduled: "Planlandı",
  in_examination: "Muayenede",
  completed: "Tamamlandı",
  cancelled: "İptal",
  no_show: "Gelmedi",
};

const scrollSx = {
  height: "100%",
  overflowY: "auto",
  overscrollBehavior: "contain",
  mx: -2,
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-thumb": { bgcolor: COLORS.slate200, borderRadius: 3 },
};

function Row({ children, onClick, palette, muted }) {
  return (
    <Box
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => onClick && e.key === "Enter" && onClick()}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1.25,
        borderBottom: `1px solid ${COLORS.slate100}`,
        cursor: onClick ? "pointer" : "default",
        opacity: muted ? 0.55 : 1,
        "&:hover": onClick ? { bgcolor: alpha(palette.primary, 0.03) } : undefined,
      }}
    >
      {children}
    </Box>
  );
}

const Main = ({ children, strike }) => (
  <Typography noWrap sx={{ fontSize: TYPO.body, fontWeight: 500, color: COLORS.slate800, textDecoration: strike ? "line-through" : "none" }}>
    {children}
  </Typography>
);
const Sub = ({ children }) => (
  <Typography noWrap sx={{ fontSize: TYPO.caption, color: COLORS.slate500 }}>
    {children}
  </Typography>
);
const Amount = ({ children }) => (
  <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
    {children}
  </Typography>
);

/**
 * @param {string} patientId
 * @param {string} [clinicId] - admin bir kliniğin hastasını açtığında
 * @param {string} token
 * @param {boolean} [canEdit=true] - muayene kilidi vb. durumda yazma kapalı
 */
export default function AccountTab({ patientId, clinicId, token, canEdit = true }) {
  const terms = useTerms();
  const { palette } = useClinicTheme();
  const { showSuccess, showError } = useAlert();
  const canSell = useHasPermission("packages:manage") && canEdit;
  const canPay = useHasPermission("payments:create") && canEdit;
  const canDeletePayment = useHasPermission("payments:delete") && canEdit;

  const [account, setAccount] = useState(null);
  const [sellOpen, setSellOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const scope = clinicId ? { clinicId } : {};
  const [getAccount, { isLoading }] = useGetPatientAccountMutation();
  const [sellPackage, { isLoading: selling }] = useSellPatientPackageMutation();
  const [consume, { isLoading: consuming }] = useConsumePatientPackageMutation();
  const [revert, { isLoading: reverting }] = useRevertPackageUsageMutation();
  const [cancelPkg, { isLoading: cancelling }] = useCancelPatientPackageMutation();
  const [createPayment, { isLoading: paying }] = useCreatePaymentMutation();
  const [deletePayment, { isLoading: deleting }] = useDeletePaymentMutation();

  const load = useCallback(async () => {
    if (!token || !patientId) return;
    try {
      const r = await getAccount({ token, patientId, ...(clinicId ? { clinicId } : {}) }).unwrap();
      setAccount(r?.data?.account || null);
    } catch (e) {
      setAccount((a) => a || { totals: { charged: 0, paid: 0, balance: 0 }, packages: [], payments: [], services: [], charges: [] });
      showError(e?.data?.message || "Hesap bilgisi alınamadı");
    }
  }, [token, patientId, clinicId, getAccount, showError]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (fn, okMsg) => {
    try {
      await fn();
      if (okMsg) showSuccess(okMsg);
      await load();
      return true;
    } catch (e) {
      showError(e?.data?.message || "İşlem tamamlanamadı");
      return false;
    }
  };

  const loading = isLoading && !account;
  const totals = account?.totals;
  const packages = account?.packages || [];
  const payments = account?.payments || [];
  const services = account?.services || [];
  const activePackages = packages.filter((p) => p.status === "active");
  const detailPkg = packages.find((p) => p._id === detailId) || null;
  const balance = totals?.balance || 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: CARD_GAP }}>
      <Box sx={{ display: "grid", gap: CARD_GAP, gridTemplateColumns: { xs: "1fr 1fr", lg: "repeat(4, 1fr)" } }}>
        <KpiTile
          loading={loading}
          label={balance < 0 ? "Avans" : "Kalan borç"}
          value={totals ? formatMoney(Math.abs(balance)) : ""}
          hint={balance > 0 ? `${terms.patientGen} ödemesi gereken` : balance < 0 ? "Fazla ödeme, sonraki hizmetlere sayılır" : "Hesap kapalı"}
          valueColor={balance > 0 ? COLORS.warningDark : undefined}
        />
        <KpiTile
          loading={loading}
          label="Hizmet ve paket bedeli"
          value={totals ? formatMoney(totals.charged) : ""}
          hint={`${account?.charges?.length || 0} kalem`}
        />
        <KpiTile
          loading={loading}
          label="Ödenen"
          value={totals ? formatMoney(totals.paid) : ""}
          hint={`${payments.filter((p) => !p.deleted).length} ödeme`}
        />
        <KpiTile
          loading={loading}
          label="Aktif paket"
          value={String(activePackages.length)}
          hint={
            activePackages.length
              ? `${activePackages.reduce((s, p) => s + (p.remaining || 0), 0)} seans hakkı kaldı`
              : "Satılmış aktif paket yok"
          }
        />
      </Box>

      <Box sx={{ display: "grid", gap: CARD_GAP, gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" } }}>
        <AnalyticsCard
          title="Paketler"
          subtitle="Satılan seans paketleri"
          icon={<PackageIcon sx={{ fontSize: 18 }} />}
          bodyHeight={BODY}
          action={
            canSell ? (
              <ActionButton variant="add" compact palette={palette} label="Paket sat" onClick={() => setSellOpen(true)} />
            ) : null
          }
        >
          {loading ? (
            <BodySkeleton kind="list" />
          ) : !packages.length ? (
            <CardEmpty
              icon={<PackageIcon />}
              title="Paket yok"
              description={canSell ? "Seans paketi satmak için başlıktaki “Paket sat”ı kullanın." : `${terms.patientDat} henüz paket satılmadı.`}
            />
          ) : (
            <Box sx={scrollSx}>
              {packages.map((p) => {
                const st = PACKAGE_STATUS[p.status] || PACKAGE_STATUS.active;
                return (
                  <Row key={p._id} palette={palette} onClick={() => setDetailId(p._id)} muted={p.status === "cancelled"}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                        <Main>{p.template?.name}</Main>
                        <StatusPill label={st.label} tone={st.tone} />
                      </Box>
                      <Sub>
                        {p.used}/{p.total} seans · {p.status === "active" ? `${dateTr(p.expiresAt)} tarihine kadar` : dateTr(p.startedAt)}
                      </Sub>
                      <LinearProgress
                        variant="determinate"
                        value={p.total ? Math.round((p.used / p.total) * 100) : 0}
                        sx={{
                          mt: 0.75,
                          height: 4,
                          borderRadius: 2,
                          bgcolor: COLORS.slate100,
                          "& .MuiLinearProgress-bar": { bgcolor: palette.primary, borderRadius: 2 },
                        }}
                      />
                    </Box>
                    <Amount>{formatMoney(p.template?.price)}</Amount>
                  </Row>
                );
              })}
            </Box>
          )}
        </AnalyticsCard>

        <AnalyticsCard
          title="Ödemeler"
          subtitle={`${terms.patient}dan alınan ödemeler`}
          icon={<PaymentIcon sx={{ fontSize: 18 }} />}
          bodyHeight={BODY}
          action={
            canPay ? (
              <ActionButton variant="add" compact palette={palette} label="Ödeme al" onClick={() => setPayOpen(true)} />
            ) : null
          }
        >
          {loading ? (
            <BodySkeleton kind="list" />
          ) : !payments.length ? (
            <CardEmpty
              icon={<PaymentIcon />}
              title="Ödeme yok"
              description={canPay ? `${terms.patient}dan ödeme aldığınızda başlıktaki “Ödeme al” ile kaydedin.` : "Henüz ödeme kaydı yok."}
            />
          ) : (
            <Box sx={scrollSx}>
              {payments.map((p) => (
                <Row key={p._id} palette={palette} muted={p.deleted}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Main strike={p.deleted}>
                      {PAYMENT_METHOD_LABELS[p.method]} · {dateTr(p.paidAt)}
                    </Main>
                    <Sub>
                      {p.deleted
                        ? `Silindi: ${p.deletedReason || "—"}`
                        : [p.note, p.recordedByName ? `Alan: ${p.recordedByName}` : null].filter(Boolean).join(" · ") || "—"}
                    </Sub>
                  </Box>
                  <Amount>{formatMoney(p.amount)}</Amount>
                  {canDeletePayment && !p.deleted ? (
                    <Tooltip title="Ödemeyi sil" arrow>
                      <IconButton
                        size="small"
                        aria-label="Ödemeyi sil"
                        onClick={() => setDeleteTarget(p)}
                        sx={{ color: COLORS.slate400, "&:hover": { color: COLORS.errorDark } }}
                      >
                        <DeleteIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </Row>
              ))}
            </Box>
          )}
        </AnalyticsCard>
      </Box>

      <AnalyticsCard
        title="Hizmet geçmişi"
        subtitle="Hizmet seçilmiş randevular · tamamlananlar paketten düşülmediyse borca yazılır"
        icon={<ServiceIcon sx={{ fontSize: 18 }} />}
        bodyHeight={300}
      >
        {loading ? (
          <BodySkeleton kind="list" />
        ) : !services.length ? (
          <CardEmpty
            icon={<ServiceIcon />}
            title="Hizmetli randevu yok"
            description="Randevu oluştururken hizmet seçildiğinde burada listelenir."
          />
        ) : (
          <Box sx={scrollSx}>
            {services.map((a) => (
              <Row key={a._id} palette={palette} muted={a.status === "cancelled"}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Main>{a.serviceName || "Hizmet"}</Main>
                  <Sub>
                    {[dateTr(a.startAt), (a.status === "in_examination" ? terms.inExam : STATUS_LABELS[a.status]) || a.status, a.snapshot?.doctorName].filter(Boolean).join(" · ")}
                  </Sub>
                </Box>
                {a.coveredByPackage ? (
                  <StatusPill label="Paketten" tone="primary" palette={palette} />
                ) : a.patientPackage && a.status !== "completed" && a.status !== "cancelled" ? (
                  <StatusPill label="Paketten düşecek" tone="neutral" />
                ) : null}
                <Amount>{a.chargeable ? formatMoney(a.servicePrice) : "—"}</Amount>
              </Row>
            ))}
          </Box>
        )}
      </AnalyticsCard>

      <SellPackageDrawer
        open={sellOpen}
        onClose={() => setSellOpen(false)}
        token={token}
        clinicId={clinicId}
        saving={selling}
        onSell={async (payload) => {
          const ok = await run(
            () => sellPackage({ token, patientId, ...scope, ...payload }).unwrap(),
            "Paket satıldı"
          );
          if (ok) setSellOpen(false);
        }}
      />
      <PaymentDrawer
        open={payOpen}
        onClose={() => setPayOpen(false)}
        saving={paying}
        suggestedAmount={balance > 0 ? balance : 0}
        packages={packages}
        onSave={async (payload) => {
          const ok = await run(
            () => createPayment({ token, patientId, ...scope, ...payload }).unwrap(),
            "Ödeme kaydedildi"
          );
          if (ok) setPayOpen(false);
        }}
      />
      <PackageDetailDrawer
        open={Boolean(detailPkg)}
        onClose={() => setDetailId(null)}
        pkg={detailPkg}
        canManage={canSell}
        busy={consuming || reverting || cancelling}
        onConsume={(p, note) =>
          run(
            () => consume({ token, ...scope, patientPackageId: p._id, note: note || undefined }).unwrap(),
            "Paketten bir seans düşüldü"
          )
        }
        onRevert={(p, u) =>
          run(
            () => revert({ token, ...scope, patientPackageId: p._id, usageId: u._id }).unwrap(),
            "Kullanım geri alındı"
          )
        }
        onCancel={(p, reason) =>
          run(() => cancelPkg({ token, ...scope, patientPackageId: p._id, reason }).unwrap(), "Paket iptal edildi")
        }
      />
      <PromptDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async (reason) => {
          const ok = await run(
            () => deletePayment({ token, ...scope, paymentId: deleteTarget._id, reason }).unwrap(),
            "Ödeme kaydı silindi"
          );
          if (ok) setDeleteTarget(null);
        }}
        title="Ödeme kaydını sil"
        message={
          deleteTarget
            ? `${formatMoney(deleteTarget.amount)} tutarındaki ${PAYMENT_METHOD_LABELS[deleteTarget.method]?.toLocaleLowerCase("tr")} ödemesi silinecek. Kayıt gerekçesiyle birlikte geçmişte görünmeye devam eder.`
            : ""
        }
        inputLabel="Silme gerekçesi"
        placeholder="Ör. yanlış tutar girildi"
        required
        minRows={2}
        maxLength={300}
        confirmText="Sil"
        cancelText="Vazgeç"
        loading={deleting}
      />
    </Box>
  );
}
